import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiCreditCard } from "react-icons/fi";
import { formatNgn } from "../../../Routes/Marketplace/libs/orderDisplayUtils";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import StatCard from "../../components/StatCard";
import StatusBadge from "../../components/StatusBadge";
import DetailDrawer from "../../components/DetailDrawer";
import DetailGrid from "../../components/DetailGrid";
import Spinner from "../../../Spinner/Spinner";
import useAdminPayouts, { useUpdateAdminPayout } from "../hooks/useAdminPayouts";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "processing", label: "Processing" },
  { id: "paid", label: "Paid" },
  { id: "rejected", label: "Rejected" },
];

const STATUS_VARIANT = {
  pending: "warning",
  processing: "warning",
  paid: "success",
  rejected: "danger",
};

const AdminPayoutsPage = () => {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  const { data, isLoading, isError, isFetching } = useAdminPayouts(page, statusFilter);
  const { mutate: updatePayout, isLoading: updating } = useUpdateAdminPayout();

  const payouts = useMemo(() => data?.payouts || [], [data?.payouts]);
  const totalPages = data?.totalPages || 1;

  const stats = useMemo(
    () => ({
      pending: data?.stats?.pending ?? 0,
      processing: data?.stats?.processing ?? 0,
      pageTotal: payouts.reduce((sum, payout) => sum + (payout.amount || 0), 0),
    }),
    [data?.stats, payouts]
  );

  const openPayout = (payout) => {
    setSelectedPayout(payout);
    setAdminNote(payout.adminNote || "");
  };

  const handleStatusUpdate = (status) => {
    if (!selectedPayout) return;

    const label =
      status === "paid" ? "mark this payout as paid" : status === "rejected" ? "reject this payout" : "mark as processing";

    if (!window.confirm(`Are you sure you want to ${label}?`)) return;

    updatePayout(
      { payoutId: selectedPayout._id, status, adminNote },
      {
        onSuccess: (payload) => {
          toast.success(payload.message || "Payout updated");
          setSelectedPayout((prev) => (prev ? { ...prev, status, adminNote } : prev));
        },
        onError: (err) => toast.error(err.response?.data?.message || "Could not update payout"),
      }
    );
  };

  const columns = [
    {
      key: "merchant",
      label: "Merchant",
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.businessName || "—"}</div>
          <div style={{ fontSize: 12, color: "var(--dash-muted)" }}>
            {[row.merchantUser?.first_name, row.merchantUser?.last_name].filter(Boolean).join(" ") ||
              row.merchantUser?.email ||
              "—"}
          </div>
        </div>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      render: (row) => formatNgn(row.amount),
    },
    {
      key: "orderCount",
      label: "Lines",
      render: (row) => row.orderCount || row.orderItemRefs?.length || 0,
    },
    {
      key: "payoutMethod",
      label: "Method",
      render: (row) => (row.payoutMethod === "mobile_money" ? "Mobile money" : "Bank transfer"),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <StatusBadge status={row.status} variant={STATUS_VARIANT[row.status] || "default"} />
      ),
    },
    {
      key: "createdAt",
      label: "Requested",
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => openPayout(row)}>
          Review
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Merchant payouts"
        subtitle="Review cash-out requests and mark transfers as paid or rejected"
        icon={FiCreditCard}
      />

      <div className="dashboard-stat-grid">
        <StatCard label="Pending review" value={stats.pending} />
        <StatCard label="Processing" value={stats.processing} />
        <StatCard label="On this page" value={formatNgn(stats.pageTotal)} />
      </div>

      <FilterBar>
        <div className="dashboard-filter-pills">
          {STATUS_FILTERS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={statusFilter === id ? "active" : ""}
              onClick={() => {
                setStatusFilter(id);
                setPage(1);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </FilterBar>

      {isLoading ? (
        <div className="dashboard-card" style={{ padding: 32, textAlign: "center" }}>
          <Spinner />
        </div>
      ) : isError ? (
        <div className="dashboard-banner dashboard-banner--danger">Could not load payout queue.</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={payouts.map((row) => ({ ...row, _key: row._id }))}
            emptyMessage="No payout requests in this queue."
          />
          {totalPages > 1 && (
            <div className="dashboard-pagination">
              <button type="button" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <DetailDrawer
        open={!!selectedPayout}
        onClose={() => setSelectedPayout(null)}
        title={selectedPayout ? `${formatNgn(selectedPayout.amount)} — ${selectedPayout.businessName || "Payout"}` : "Payout"}
      >
        {selectedPayout && (
          <>
            <DetailGrid
              items={[
                { label: "Merchant", value: selectedPayout.businessName || "—" },
                {
                  label: "Contact",
                  value:
                    [selectedPayout.merchantUser?.first_name, selectedPayout.merchantUser?.last_name]
                      .filter(Boolean)
                      .join(" ") || selectedPayout.merchantUser?.email,
                },
                { label: "Status", value: selectedPayout.status },
                { label: "Destination", value: selectedPayout.payoutDestination || "—" },
                {
                  label: "Requested",
                  value: new Date(selectedPayout.createdAt).toLocaleString(),
                },
                selectedPayout.paidAt
                  ? { label: "Paid at", value: new Date(selectedPayout.paidAt).toLocaleString() }
                  : null,
              ].filter(Boolean)}
            />

            {selectedPayout.orderItemRefs?.length > 0 && (
              <div className="dashboard-card" style={{ marginTop: 16 }}>
                <h3 className="dashboard-card__title">Linked order lines</h3>
                <ul className="dashboard-payout-order-list">
                  {selectedPayout.orderItemRefs.map((ref) => (
                    <li key={`${selectedPayout._id}-${ref.itemId}`}>
                      <span>{ref.productName}</span>
                      <span>{ref.orderNumber ? `#${ref.orderNumber}` : ""}</span>
                      <span>Qty {ref.quantity}</span>
                      <strong>{formatNgn(ref.amount)}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <label className="dashboard-form-field dashboard-form-field--full" style={{ marginTop: 16 }}>
              <span>Admin note</span>
              <textarea
                rows={3}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Optional note for internal records or rejection reason"
              />
            </label>

            {selectedPayout.status !== "paid" && selectedPayout.status !== "rejected" && (
              <div className="dashboard-form-actions" style={{ marginTop: 16 }}>
                {selectedPayout.status === "pending" && (
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--secondary"
                    disabled={updating}
                    onClick={() => handleStatusUpdate("processing")}
                  >
                    Mark processing
                  </button>
                )}
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--primary"
                  disabled={updating}
                  onClick={() => handleStatusUpdate("paid")}
                >
                  Mark paid
                </button>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--danger"
                  disabled={updating}
                  onClick={() => handleStatusUpdate("rejected")}
                >
                  Reject
                </button>
              </div>
            )}
          </>
        )}
      </DetailDrawer>
    </>
  );
};

export default AdminPayoutsPage;
